import { z } from 'zod';
import {
  appointmentReminderLeadHours,
  countries,
  emailAccentColors,
  languages,
  type AppointmentReminderLeadHours,
  type Country,
  type EmailAccentColor,
  type Language,
  type MemberRole,
} from '../enums.js';
import { emailSchema } from '../ids.js';

/**
 * Contrat réseau du cabinet lui-même et de ses sites, partagé mot pour mot entre le serveur et le
 * front. Schémas d'entrée écrits à la main et `.strict()` : jamais dérivés d'une table, sinon
 * `publicRegistrationToken` ou `logoStorageKey` deviendraient modifiables depuis le réseau.
 */

const trimmed = (max: number) => z.string().trim().min(1).max(max);

/** Champ facultatif : la chaîne vide vaut « effacé », donc `null` en base. */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable();

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

/** Signature des courriels du cabinet : texte brut, quelques lignes, jamais du HTML. */
export const EMAIL_SIGNATURE_MAX_LENGTH = 500;
export const EMAIL_SIGNATURE_MAX_LINES = 8;

/** Aucun caractère de contrôle hormis le retour à la ligne : une signature n'a rien à cacher. */
function hasOnlyPrintable(value: string): boolean {
  return Array.from(value).every((char) => {
    const code = char.charCodeAt(0);
    return char === '\n' || (code >= 0x20 && code !== 0x7f);
  });
}

/**
 * Retours à la ligne normalisés, espaces de fin de ligne retirés ; la chaîne vide vaut « aucune
 * signature ». Les bornes portent sur le texte normalisé, celui qui sera enregistré.
 */
const emailSignatureSchema = z
  .string()
  .transform((value) =>
    value
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map((line) => line.trimEnd())
      .join('\n')
      .trim(),
  )
  .pipe(
    z
      .string()
      .max(EMAIL_SIGNATURE_MAX_LENGTH)
      .refine((value) => value.split('\n').length <= EMAIL_SIGNATURE_MAX_LINES)
      .refine(hasOnlyPrintable),
  )
  .transform((value) => (value === '' ? null : value))
  .nullable();

/**
 * Champs du cabinet modifiables par son propriétaire. Le pays reste hors de cette liste : le
 * périmètre V2.0 est luxembourgeois et le pays pilote la numérotation et les mentions légales.
 */
export const organizationUpdateSchema = z
  .object({
    name: trimmed(200),
    legalName: optionalText(200),
    rcsNumber: optionalText(40),
    vatNumber: optionalText(30),
    addressLine1: optionalText(160),
    addressLine2: optionalText(160),
    postalCode: optionalText(20),
    city: optionalText(120),
    phone: optionalText(40),
    email: emailSchema.nullable(),
    website: optionalText(200),
    preferredLanguage: z.enum(languages),
    customLegalMentionsPrimary: optionalText(2000),
    customLegalMentionsTax: optionalText(2000),
    /**
     * Page d'avis public du cabinet — Google Maps en pratique. C'est elle qui décide : sans
     * adresse où envoyer, aucun avis n'est proposé au patient, quel que soit l'interrupteur.
     */
    reviewUrl: optionalText(500),
    reviewRequestEnabled: z.boolean(),
    /**
     * Rappels de rendez-vous par courriel. Éteints par défaut : un cabinet décide lui-même
     * d'écrire à ses patients. `appointmentRemindersSkipDoctena` écarte les rendez-vous venus de
     * Doctena, qui envoie déjà les siens — allumé par défaut, pour qu'un patient n'en reçoive pas
     * deux.
     */
    appointmentRemindersEnabled: z.boolean(),
    appointmentReminderLeadHours: z.literal(appointmentReminderLeadHours),
    appointmentRemindersSkipDoctena: z.boolean(),
    /** Teinte d'accent des courriels, par son nom ; `null` revient à l'apparence d'origine. */
    emailAccentColor: z.enum(emailAccentColors).nullable(),
    emailSignature: emailSignatureSchema,
  })
  .partial()
  .strict();
export type OrganizationUpdateInput = z.infer<typeof organizationUpdateSchema>;

export const locationCreateSchema = z
  .object({
    name: trimmed(160),
    addressLine1: optionalText(160).optional(),
    addressLine2: optionalText(160).optional(),
    postalCode: optionalText(20).optional(),
    city: optionalText(120).optional(),
    country: z.enum(countries).optional(),
    phone: optionalText(40).optional(),
    isMain: z.boolean().optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type LocationCreateInput = z.infer<typeof locationCreateSchema>;

export const locationUpdateSchema = locationCreateSchema.partial().strict();
export type LocationUpdateInput = z.infer<typeof locationUpdateSchema>;

export const locationIdParamSchema = z.object({ locationId: z.uuid() }).strict();

/* -------------------------------------------------------------------------- */
/* Sorties : objets de transfert explicites, champ par champ.                   */
/* -------------------------------------------------------------------------- */

export interface OrganizationDto {
  id: string;
  name: string;
  legalName: string | null;
  country: Country;
  rcsNumber: string | null;
  vatNumber: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  preferredLanguage: Language;
  customLegalMentionsPrimary: string | null;
  customLegalMentionsTax: string | null;
  reviewUrl: string | null;
  reviewRequestEnabled: boolean;
  appointmentRemindersEnabled: boolean;
  appointmentReminderLeadHours: AppointmentReminderLeadHours;
  appointmentRemindersSkipDoctena: boolean;
  emailAccentColor: EmailAccentColor | null;
  emailSignature: string | null;
  /** Le chemin de stockage ne sort jamais : le client ne sait que si un logo existe. */
  hasLogo: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Aperçu d'un courriel du cabinet (`GET /api/organizations/current/email-preview`) : le vrai
 * gabarit de rappel, rendu par le serveur avec un rendez-vous fictif. Aucune donnée patient.
 * Le logo y est intégré à la page, puisqu'un navigateur ne lit pas les pièces jointes.
 */
export interface OrganizationEmailPreviewDto {
  subject: string;
  html: string;
}

/** Réponse de `GET /api/organizations/current` : le cabinet actif et le rôle de l'appelant. */
export interface CurrentOrganizationDto {
  organization: OrganizationDto;
  role: MemberRole;
}

/* -------------------------------------------------------------------------- */
/* Logo du cabinet                                                             */
/* -------------------------------------------------------------------------- */

/**
 * 2 Mo. Un logo est une image d'en-tête de facture, pas une photographie : au-delà, c'est un
 * fichier mal préparé, et il alourdirait chaque PDF émis.
 */
export const ORGANIZATION_LOGO_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Types acceptés, vérifiés sur les **octets magiques** et non sur l'en-tête annoncé.
 *
 * **Pas de SVG**, bien qu'un logo vectoriel soit tentant : un SVG est un document XML qui peut
 * embarquer du script, et il finirait servi à un navigateur. Les trois formats retenus sont des
 * images matricielles que ni la détection de type ni le rendu PDF ne peuvent confondre avec du
 * code.
 */
export const organizationLogoMimeTypes = ['image/png', 'image/jpeg', 'image/webp'] as const;
export type OrganizationLogoMimeType = (typeof organizationLogoMimeTypes)[number];

/**
 * Le même logo figure en tête des courriels que le cabinet envoie à ses patients et
 * correspondants, joint au message — une image distante serait un pixel espion. Il y voyage à
 * chaque envoi, d'où une borne bien plus basse que pour l'impression : au-delà, le nom du cabinet
 * prend sa place.
 */
export const ORGANIZATION_LOGO_EMAIL_MAX_BYTES = 250 * 1024;

/**
 * Formats lus par tous les clients de messagerie. Le WebP en est écarté : Outlook pour Windows ne
 * l'affiche pas, et un en-tête cassé ferait plus douter du message qu'un nom bien écrit.
 */
export const organizationEmailLogoMimeTypes = ['image/png', 'image/jpeg'] as const;
export type OrganizationEmailLogoMimeType = (typeof organizationEmailLogoMimeTypes)[number];

/** Ce qu'un téléversement de logo rend : de quoi rafraîchir l'aperçu sans relire le cabinet. */
export interface OrganizationLogoDto {
  hasLogo: boolean;
  mimeType: OrganizationLogoMimeType | null;
  sizeBytes: number | null;
  updatedAt: string;
}

export interface LocationDto {
  id: string;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  country: Country;
  phone: string | null;
  isMain: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}
